import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { parsePhotoAnnotation, type PhotoAnnotation } from '@qms360/shared';
import { ApiError } from '../../lib/api.js';
import { resizeImage } from '../../lib/offline/capture.js';
import {
  clearQueuedPhoto,
  listQueuedPhotos,
  queuePhoto,
  type QueuedPhoto,
} from '../../lib/offline/outbox.js';
import { annotatePhoto, type LessonPhoto, type PhotoCategory } from '../../lib/lesson-api.js';
import { AnnotationOverlay, describeAnnotation } from './AnnotationOverlay.js';
import { PhotoAnnotator } from './PhotoAnnotator.js';
import { StoredImage } from './StoredImage.js';
import { t } from '../../lib/i18n.js';

/**
 * Before/after photo evidence (spec §10).
 *
 * Every photo goes through the outbox, even with a perfect connection. That is
 * the deliberate choice: one path means the offline case is exercised constantly
 * rather than only on the day it is needed, and a photo can never be captured
 * into a code path that assumes a network it might lose mid-upload.
 *
 * The resize to 1920x1080 happens before the bytes are queued, not before they
 * are uploaded. A phone camera produces several megabytes a shot, and ten of
 * those sitting in IndexedDB is a queue that will not clear over a site's signal.
 */

const MAX_PER_CATEGORY = 5;

const CATEGORY_LABELS: Record<PhotoCategory, string> = {
  before: 'Before',
  after: 'After',
};

export interface PhotoCaptureProps {
  lessonId: string;
  category: PhotoCategory;
  /** Already on the server. */
  uploaded: LessonPhoto[];
  readOnly?: boolean;
  onQueued?: () => void;
}

export function PhotoCapture({
  lessonId,
  category,
  uploaded,
  readOnly = false,
  onQueued,
}: PhotoCaptureProps) {
  const [queued, setQueued] = useState<QueuedPhoto[]>([]);
  const queryClient = useQueryClient();
  const [annotating, setAnnotating] = useState<LessonPhoto | null>(null);
  const [annotateError, setAnnotateError] = useState<string | null>(null);
  const annotate = useMutation({
    mutationFn: ({ photo, annotation }: { photo: LessonPhoto; annotation: PhotoAnnotation | null }) =>
      annotatePhoto(lessonId, photo.id, annotation),
    onSuccess: () => {
      setAnnotating(null);
      void queryClient.invalidateQueries({ queryKey: ['lesson', lessonId] });
    },
    onError: (caught) =>
      setAnnotateError(
        caught instanceof ApiError ? caught.message : t("The marks could not be saved. Drawing needs a signal."),
      ),
  });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    const all = await listQueuedPhotos(lessonId);
    setQueued(all.filter((photo) => photo.category === category));
  };

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessonId, category]);

  const total = uploaded.length + queued.length;
  const full = total >= MAX_PER_CATEGORY;

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setError(null);
    setBusy(true);

    try {
      const room = MAX_PER_CATEGORY - total;
      if (files.length > room) {
        setError(
          t("Only {0} more {1} {2} can be added. The rest were not taken.", { 0: room, 1: CATEGORY_LABELS[category].toLowerCase(), 2: room === 1 ? 'photo' : 'photos' }),
        );
      }

      for (const file of Array.from(files).slice(0, Math.max(room, 0))) {
        const resized = await resizeImage(file);
        await queuePhoto({
          localId: crypto.randomUUID(),
          formId: lessonId,
          category,
          filename: file.name || `${category}-${Date.now()}.jpg`,
          contentType: resized.contentType,
          data: resized.data,
          caption: null,
          capturedAt: new Date().toISOString(),
        });
      }

      await refresh();
      onQueued?.();
    } catch {
      setError(t("That photo could not be prepared. Try taking it again."));
    } finally {
      setBusy(false);
    }
  };

  const discardQueued = async (localId: string) => {
    // Only ever a photo that has not left the device. Anything the server holds
    // is archived through the API, never removed here (Rule 2).
    await clearQueuedPhoto(localId);
    await refresh();
  };

  const inputId = `photo-${category}-${lessonId}`;

  return (
    <div className="rounded-md border border-border bg-surface p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="m-0 text-sm font-semibold">{t(CATEGORY_LABELS[category])}{' '}{t("photos")}</h3>
        <span className="text-xs text-content-secondary">
          {total}{' '}{t("of")}{' '}{MAX_PER_CATEGORY}
        </span>
      </div>

      {!readOnly && (
        <>
          <label
            htmlFor={inputId}
            className={`mt-3 inline-block cursor-pointer rounded-sm border border-border-strong px-3 py-1.5 text-sm font-medium ${
              full || busy ? 'pointer-events-none opacity-50' : ''
            }`}
          >
            {busy ? t("Preparing…") : t("Take or choose a {0} photo", { 0: CATEGORY_LABELS[category].toLowerCase() })}
          </label>
          <input
            id={inputId}
            type="file"
            accept="image/*"
            // Opens the camera directly on a phone rather than the file browser.
            capture="environment"
            multiple
            disabled={full || busy}
            onChange={(event) => {
              void handleFiles(event.target.files);
              event.target.value = '';
            }}
            className="sr-only"
          />
        </>
      )}

      {error && (
        <p role="alert" className="mt-2 text-xs" style={{ color: 'var(--tone-rejected)' }}>
          {error}
        </p>
      )}

      {total === 0 && <p className="mt-3 text-sm text-content-secondary">{t("No photos yet.")}</p>}

      <ul className="mt-3 grid grid-cols-2 gap-2 p-0 sm:grid-cols-3">
        {queued.map((photo) => (
          <li key={photo.localId} className="list-none">
            <figure className="m-0">
              <img
                src={`data:${photo.contentType};base64,${photo.data}`}
                alt={t("{0} photo waiting to sync", { 0: CATEGORY_LABELS[category] })}
                className="aspect-video w-full rounded-sm object-cover"
              />
              <figcaption className="mt-1 flex items-center justify-between text-xs text-content-secondary">
                <span>{photo.lastError ? t("Rejected") : t("Waiting to sync")}</span>
                {!readOnly && (
                  <button
                    type="button"
                    onClick={() => void discardQueued(photo.localId)}
                    className="underline"
                  >
                    {t("Discard")}
                  </button>
                )}
              </figcaption>
            </figure>
          </li>
        ))}

        {uploaded.map((photo) => {
          const annotation = parsePhotoAnnotation(photo.annotation);
          const alt = photo.caption ?? `${CATEGORY_LABELS[category]} photo`;
          const marks = describeAnnotation(annotation);
          return (
            <li key={photo.id} className="list-none">
              <figure className="m-0">
                {/* Uncropped, so the marks sit where they were drawn. */}
                <div className="relative [container-type:inline-size]">
                  <StoredImage fileId={photo.fileId} alt={marks ? t("{0}, with {1}", { 0: alt, 1: marks }) : alt} className="block w-full rounded-sm" />
                  <AnnotationOverlay annotation={annotation} />
                </div>
                <figcaption className="mt-1 flex items-center gap-2 text-xs text-content-secondary">
                  <span>{t("Synced")}</span>
                  {!readOnly && (
                    <button
                      type="button"
                      className="rounded-sm border border-border-strong px-2 py-0.5 text-content-primary"
                      onClick={() => {
                        setAnnotateError(null);
                        setAnnotating(photo);
                      }}
                    >
                      {annotation ? t("Edit the marks") : t("Draw on it")}
                    </button>
                  )}
                </figcaption>
              </figure>
            </li>
          );
        })}
      </ul>

      {annotating && (
        <PhotoAnnotator
          fileId={annotating.fileId}
          alt={annotating.caption ?? t("{0} photo", { 0: CATEGORY_LABELS[category] })}
          initial={parsePhotoAnnotation(annotating.annotation)}
          busy={annotate.isPending}
          error={annotateError}
          onSave={(annotation) => annotate.mutate({ photo: annotating, annotation })}
          onCancel={() => setAnnotating(null)}
        />
      )}
    </div>
  );
}
